import {
  Module,
  ActionTree,
  MutationTree,
  GetterTree,
} from 'vuex';
import Axios from 'axios';
import JwtDecode from 'jwt-decode';
import LoginCommand from '@/use-cases/user-auth/LoginCommand';
import SecurityTokenDTO from './SecurityTokenDTO';
import SecurityToken from './SecurityToken';

export interface AuthState {
  status: string;
  token: string | null;
}

const TOKEN_KEY = 'jwt';
const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
const LOGIN_ERROR = 'LOGIN_ERROR';

export const LOGIN_REQUEST = 'LOGIN_REQUEST';
export const LOGOUT = 'LOGOUT';
export const LOGIN_INITIALISE = 'LOGIN_INITIALISE';

const authState: AuthState = {
  status: '',
  token: null,
};

const mutations: MutationTree<AuthState> = {
  [LOGIN_REQUEST]: (state) => {
    state.status = 'loading';
  },
  [LOGIN_SUCCESS]: (state, token) => {
    state.status = 'success';
    state.token = token;
  },
  [LOGIN_ERROR]: (state) => {
    state.status = 'error';
    state.token = null;
  },
  [LOGOUT]: (state) => {
    state.status = '';
    state.token = null;
  },
};

const actions: ActionTree<AuthState, object> = {
  [LOGIN_REQUEST]: ({ commit }, command: LoginCommand) => new Promise<void>((resolve, reject) => {
    commit(LOGIN_REQUEST);

    Axios.post<SecurityTokenDTO>('users/login', command)
      .then(({ data }) => {
        const { token } = data;

        localStorage.setItem(TOKEN_KEY, token);
        Axios.defaults.headers.common.Authorization = `Bearer ${token}`;
        commit(LOGIN_SUCCESS, token);

        return resolve();
      })
      .catch((err) => {
        commit(LOGIN_ERROR, err);
        localStorage.removeItem(TOKEN_KEY);

        return reject(err);
      });
  }),
  [LOGOUT]: ({ commit }) => new Promise<void>((resolve) => {
    commit(LOGOUT);
    localStorage.removeItem(TOKEN_KEY);
    delete Axios.defaults.headers.common.Authorization;

    return resolve();
  }),
  [LOGIN_INITIALISE]: ({ commit }) => new Promise<void>((resolve) => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (token) {
      commit(LOGIN_SUCCESS, token);
      Axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }

    return resolve();
  }),
};

const getters: GetterTree<AuthState, object> = {
  isAuthenticated(state) {
    try {
      const token = JwtDecode<SecurityToken>(state.token as string);
      const isCurrent = token.exp * 1000 > Date.now();

      return !!state.token && isCurrent;
    } catch (e) {
      return false;
    }
  },
  userRole(state) {
    try {
      const token = JwtDecode<SecurityToken>(state.token as string);

      return token.role;
    } catch {
      return null;
    }
  },
  userId(state) {
    try {
      const token = JwtDecode<SecurityToken>(state.token as string);

      return token.nameid;
    } catch {
      return null;
    }
  },
};

const authModule: Module<AuthState, object> = {
  state: authState,
  mutations,
  actions,
  getters,
};

export default authModule;
